import DOMPurify from 'dompurify';
import { isHostedMedia, mediaUuid } from './media';

const PLACEHOLDER_REGEX = /media:\/\/([0-9a-f-]+)/gi;

// Inline media tags arrive with `media://{uuid}` placeholder sources; the real
// signed URL lives in the entry's media array. Must run BEFORE sanitizing —
// DOMPurify strips src attributes with unknown URI schemes like media://.
export const resolveMediaPlaceholders = (html, media = []) =>
  (html || '').replace(PLACEHOLDER_REGEX, (placeholder, uuid) => {
    const item = media.find((entry) => mediaUuid(entry) === uuid.toLowerCase());
    if (!item) return placeholder;
    return item.signedUrl || (!isHostedMedia(item) ? item.url : placeholder);
  });

// Ids of all media referenced inline in the html (before placeholder resolution)
export const extractMediaPlaceholderIds = (html) =>
  new Set(
    [...(html || '').matchAll(PLACEHOLDER_REGEX)].map((match) =>
      match[1].toLowerCase()
    )
  );

export const sanitizeDocumentationHtml = (html) =>
  DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'style'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover'],
  });

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-+|-+$/g, '') || 'section';

// H2/H3 build the chapter outline (h2 = main section, h3 = sub section).
// Editor html carries no heading ids, so missing ones are generated from the
// text and injected into the html as scroll targets. Call with sanitized html.
export const extractArticleOutline = (sanitizedHtml) => {
  const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  const usedIds = new Set();
  const headings = [...doc.querySelectorAll('h2, h3')].map((el) => {
    if (!el.id) {
      const base = slugify(el.textContent);
      let candidate = base;
      let counter = 2;
      while (usedIds.has(candidate)) candidate = `${base}-${counter++}`;
      el.id = candidate;
    }
    usedIds.add(el.id);
    return { id: el.id, text: el.textContent, level: Number(el.tagName[1]) };
  });
  return { html: doc.body.innerHTML, headings };
};

// Groups the flat outline for the chapter overview: h2 entries become sections
// carrying their following h3 entries as children; a leading h3 without a
// preceding h2 becomes its own section.
export const groupOutline = (headings) => {
  const sections = [];
  headings.forEach((heading) => {
    const last = sections[sections.length - 1];
    if (heading.level === 3 && last?.level === 2) last.children.push(heading);
    else sections.push({ ...heading, children: [] });
  });
  return sections;
};
